<template>
  <div class="dashboard-container p-4">
    <!-- Screenshot Header (only visible during screenshot capture) -->
    <div class="screenshot-header" :class="{ hidden: !showScreenshotHeader }">
      <div class="maze-pattern">
        <div class="header-content">
          <div class="workspace-info">
            <img
              v-if="currentWorkspaceLogo"
              :src="currentWorkspaceLogo"
              alt="Workspace Logo"
              class="workspace-logo"
            />
            <span class="workspace-name">{{
              currentWorkspaceName || "Workspace"
            }}</span>
          </div>
          <div class="date-text">{{ currentDate }}</div>
        </div>
      </div>
    </div>

    <!-- Stats Overview -->
    <div class="stats-overview mb-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-lg font-bold">Profile Completeness Overview</h2>
        <div class="flex justify-end items-center gap-2">
          <v-btn
            color="primary"
            icon
            @click="captureScreenshot"
            title="Save dashboard as image"
          >
            <v-icon>mdi-download-box-outline</v-icon>
          </v-btn>
          <div
            class="bg-primary-100 text-primary-800 px-3 py-1 rounded-full font-bold"
          >
            Total Locations: {{ filteredLocations.length }}
          </div>
        </div>
      </div>

      <!-- Overall Completeness Progress -->
      <div class="bg-white rounded-lg p-4">
        <div class="flex justify-between items-center mb-2">
          <h3 class="font-bold text-slate-700">Overall Profile Completeness</h3>
          <!-- <div class="font-bold text-primary-600">{{ overallCompleteness }}%</div> -->
        </div>
        <!-- <div class="relative h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                        class="absolute top-0 left-0 h-full bg-primary"
                        :style="`width: ${overallCompleteness}%`"
                    ></div>
                </div> -->
        <!-- <div class="mt-3 text-sm text-slate-600">
                    {{ completeProfilesCount }} of {{ filteredLocations.length }} profiles are fully complete
                </div> -->
      </div>

      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <div
          v-if="!filteredLocations.length"
          class="col-span-full bg-amber-50 p-6 rounded-lg border border-amber-200 flex flex-col items-center justify-center"
        >
          <v-icon color="amber darken-2" size="36" class="mb-3"
            >mdi-alert-circle-outline</v-icon
          >
          <p class="text-amber-700 font-medium text-center">
            No profiles available
          </p>
          <p class="text-amber-600 text-sm text-center mt-2">
            Connect Google Business Profiles to see completeness metrics
          </p>
        </div>

        <template v-else>
          <!-- Phone Number -->
          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="phoneNumber"
            :class="{ 'cursor-pointer': missingFieldsCount.phoneNumbers > 0 }"
            @click="
              missingFieldsCount.phoneNumbers > 0 &&
                openMissingFieldDialog('phoneNumbers')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Phone Number</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.phoneNumbers === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.phoneNumbers
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.phoneNumbers === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.phoneNumbers === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.phoneNumbers / filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>

          <!-- Description -->
          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="description"
            :class="{ 'cursor-pointer': missingFieldsCount.description > 0 }"
            @click="
              missingFieldsCount.description > 0 &&
                openMissingFieldDialog('description')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Description</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.description === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.description
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.description === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.description === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.description / filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>

          <!-- Website URI -->
          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="websiteUri"
            :class="{ 'cursor-pointer': missingFieldsCount.websiteUri > 0 }"
            @click="
              missingFieldsCount.websiteUri > 0 &&
                openMissingFieldDialog('websiteUri')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Website Link</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.websiteUri === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.websiteUri
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.websiteUri === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.websiteUri === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.websiteUri / filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>

          <!-- Primary Category -->
          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="primaryCategory"
            :class="{
              'cursor-pointer': missingFieldsCount.primaryCategory > 0,
            }"
            @click="
              missingFieldsCount.primaryCategory > 0 &&
                openMissingFieldDialog('primaryCategory')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Primary Category</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.primaryCategory === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.primaryCategory
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.primaryCategory === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.primaryCategory === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.primaryCategory /
                    filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>

          <!-- Additional Categories -->
          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="additionalCategories"
            :class="{
              'cursor-pointer': missingFieldsCount.additionalCategories > 0,
            }"
            @click="
              missingFieldsCount.additionalCategories > 0 &&
                openMissingFieldDialog('additionalCategories')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Add. Categories</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.additionalCategories === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.additionalCategories
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.additionalCategories === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.additionalCategories === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.additionalCategories /
                    filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>

          <!-- Regular Hours -->
          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="regularHours"
            :class="{ 'cursor-pointer': missingFieldsCount.regularHours > 0 }"
            @click="
              missingFieldsCount.regularHours > 0 &&
                openMissingFieldDialog('regularHours')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Opening Hours</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.regularHours === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.regularHours
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.regularHours === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.regularHours === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.regularHours / filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>

          <!-- Social Links -->
          <!-- <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="socialLinks"
            :class="{ 'cursor-pointer': missingFieldsCount.socialLinks > 0 }"
            @click="
              missingFieldsCount.socialLinks > 0 &&
                openMissingFieldDialog('socialLinks')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Social Links</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.socialLinks === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.socialLinks
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.socialLinks === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.socialLinks === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.socialLinks / filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div> -->

          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="gmbAttributes"
            :class="{ 'cursor-pointer': missingFieldsCount.gmbAttributes > 0 }"
            @click="
              missingFieldsCount.gmbAttributes > 0 &&
                openMissingFieldDialog('gmbAttributes')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Attributes</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.gmbAttributes === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.gmbAttributes
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.gmbAttributes === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.gmbAttributes === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.gmbAttributes /
                    filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>

          <!-- Opening Date -->
          <!-- <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="openingDate"
            :class="{ 'cursor-pointer': missingFieldsCount.openingDate > 0 }"
            @click="
              missingFieldsCount.openingDate > 0 &&
                openMissingFieldDialog('openingDate')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Opening Date</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.openingDate === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.openingDate
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.openingDate === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.openingDate === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.openingDate / filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div> -->

          <!-- Business Logo -->
          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="businessLogo"
            :class="{ 'cursor-pointer': missingFieldsCount.businessLogo > 0 }"
            @click="
              missingFieldsCount.businessLogo > 0 &&
                openMissingFieldDialog('businessLogo')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Business Logo</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.businessLogo === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.businessLogo
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.businessLogo === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.businessLogo === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.businessLogo / filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>

          <!-- Cover Photo -->
          <div
            class="stat-card bg-white rounded-lg shadow-sm p-4"
            id="coverPhoto"
            :class="{ 'cursor-pointer': missingFieldsCount.coverPhoto > 0 }"
            @click="
              missingFieldsCount.coverPhoto > 0 &&
                openMissingFieldDialog('coverPhoto')
            "
          >
            <div class="flex justify-between items-start mb-3">
              <h3 class="font-bold">Cover Photo</h3>
              <div class="status">
                <v-icon
                  v-if="missingFieldsCount.coverPhoto === 0"
                  color="success"
                  >mdi-check-circle</v-icon
                >
                <span v-else class="text-xl font-bold text-red-500">{{
                  missingFieldsCount.coverPhoto
                }}</span>
              </div>
            </div>
            <div
              v-if="missingFieldsCount.coverPhoto === 0"
              class="text-green-500"
            >
              All Profiles Complete
            </div>
            <div v-else class="text-red-500">Listings Missing</div>
            <div class="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="
                  missingFieldsCount.coverPhoto === 0
                    ? 'bg-green-500'
                    : 'bg-red-500'
                "
                :style="`width: ${
                  100 -
                  (missingFieldsCount.coverPhoto / filteredLocations.length) *
                    100
                }%`"
              ></div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Profile Health Stats -->
    <div class="additional-stats mb-6 bg-white rounded-lg p-4 shadow-sm">
      <h2 class="text-lg font-bold mb-3">Profile Health Overview</h2>
      <div
        v-if="!filteredLocations.length"
        class="bg-amber-50 p-6 rounded-lg border border-amber-200 flex flex-col items-center justify-center"
      >
        <v-icon color="amber darken-2" size="36" class="mb-3"
          >mdi-alert-circle-outline</v-icon
        >
        <p class="text-amber-700 font-medium text-center">
          No profiles available
        </p>
        <p class="text-amber-600 text-sm text-center mt-2">
          Connect Google Business Profiles to see health metrics
        </p>
      </div>
      <div v-else>
        <!-- Health Distribution Visualization -->
        <div class="mb-6">
          <div
            class="health-distribution-chart h-16 w-full bg-gray-100 rounded-md overflow-hidden flex"
          >
            <div
              class="h-full bg-green-500"
              :style="`width: ${
                (completeProfilesCount / filteredLocations.length) * 100
              }%`"
              :title="`Complete: ${completeProfilesCount} (${Math.round(
                (completeProfilesCount / filteredLocations.length) * 100
              )}%)`"
            ></div>
            <div
              class="h-full bg-amber-500"
              :style="`width: ${
                (needsAttentionCount / filteredLocations.length) * 100
              }%`"
              :title="`Needs Attention: ${needsAttentionCount} (${Math.round(
                (needsAttentionCount / filteredLocations.length) * 100
              )}%)`"
            ></div>
            <div
              class="h-full bg-red-500"
              :style="`width: ${
                (needsReauthCount / filteredLocations.length) * 100
              }%`"
              :title="`Needs Authentication: ${needsReauthCount} (${Math.round(
                (needsReauthCount / filteredLocations.length) * 100
              )}%)`"
            ></div>
          </div>
          <div class="flex justify-between mt-2">
            <div class="flex items-center text-sm">
              <div class="w-4 h-4 mr-1 rounded-sm bg-green-500"></div>
              <span>Complete ({{ completeProfilesCount }})</span>
            </div>
            <div class="flex items-center text-sm">
              <div class="w-4 h-4 mr-1 rounded-sm bg-amber-500"></div>
              <span>Needs Attention ({{ needsAttentionCount }})</span>
            </div>
            <div class="flex items-center text-sm">
              <div class="w-4 h-4 mr-1 rounded-sm bg-red-500"></div>
              <span>Auth Required ({{ needsReauthCount }})</span>
            </div>
          </div>
        </div>

        <!-- Detailed Health Stats -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            class="stat"
            :class="{ 'cursor-pointer': completeProfilesCount < 0 }"
            @click="completeProfilesCount > 0 && false"
          >
            <h3 class="text-gray-600">Complete Profiles</h3>
            <div class="text-2xl font-bold text-green-600">
              {{ completeProfilesCount }}
            </div>
            <div class="text-sm text-gray-500">
              {{
                filteredLocations.length > 0
                  ? Math.round(
                      (completeProfilesCount / filteredLocations.length) * 100
                    )
                  : 0
              }}% of total
            </div>
          </div>
          <div
            class="stat"
            :class="{ 'cursor-pointer': needsAttentionCount < 0 }"
            @click="needsAttentionCount > 0 && false"
          >
            <h3 class="text-gray-600">Profiles Needing Attention</h3>
            <div class="text-2xl font-bold text-amber-600">
              {{ needsAttentionCount }}
            </div>
            <div class="text-sm text-gray-500">
              {{
                filteredLocations.length > 0
                  ? Math.round(
                      (needsAttentionCount / filteredLocations.length) * 100
                    )
                  : 0
              }}% of total
            </div>
          </div>
          <div
            class="stat"
            :class="{ 'cursor-pointer': needsReauthCount < 0 }"
            @click="needsReauthCount > 0 && false"
          >
            <h3 class="text-gray-600">Authentication Required</h3>
            <div class="text-2xl font-bold text-red-600">
              {{ needsReauthCount }}
            </div>
            <div class="text-sm text-gray-500">
              Action needed to restore access
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Profile Completion Heatmap -->
    <div class="profile-heatmap mb-6 bg-white rounded-lg p-4 shadow-sm">
      <div class="flex flex-col gap-4 mb-4">
        <div class="flex justify-between items-center">
          <h2 class="text-lg font-bold">Profile Completion Heatmap</h2>
          <v-text-field
            v-model="heatmapSearch"
            append-icon="mdi-magnify"
            label="Search by location name or address..."
            single-line
            hide-details
            dense
            outlined
            class="max-w-xs"
          ></v-text-field>
        </div>

        <!-- Location Filter Indicator -->
        <div
          v-if="hasLocationFilter && !isDashboardSection"
          class="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between"
        >
          <div class="flex items-center">
            <v-icon color="primary" class="mr-2">mdi-filter</v-icon>
            <span class="text-blue-700">
              Showing data for:
              <span class="font-medium">{{ filteredLocationName }}</span>
            </span>
          </div>
          <v-btn text small color="primary" @click="clearLocationFilter">
            <v-icon small class="mr-1">mdi-close</v-icon>
            Clear Filter
          </v-btn>
        </div>

        <!-- Completion Range Filters -->
        <div class="flex gap-3 items-center">
          <span class="text-sm font-medium text-gray-700"
            >Filter by completion:</span
          >
          <div class="flex gap-2">
            <v-btn
              small
              :color="completionFilter === 'all' ? 'primary' : ''"
              @click="setCompletionFilter('all')"
              outlined
            >
              All
            </v-btn>
            <v-btn
              small
              :color="completionFilter === 'high' ? 'success' : ''"
              @click="setCompletionFilter('high')"
              outlined
            >
              80-100%
            </v-btn>
            <v-btn
              small
              :color="completionFilter === 'medium' ? 'warning' : ''"
              @click="setCompletionFilter('medium')"
              outlined
            >
              50-79%
            </v-btn>
            <v-btn
              small
              :color="completionFilter === 'low' ? 'error' : ''"
              @click="setCompletionFilter('low')"
              outlined
            >
              0-49%
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <v-data-table
        :headers="heatmapHeaders"
        :items="filteredHeatmapData"
        :search="heatmapSearch"
        :items-per-page="50"
        class="elevation-1 profile-heatmap-table"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        multi-sort
        :footer-props="{
          'items-per-page-options': [10, 25, 50, -1],
          'items-per-page-text': 'Locations per page',
        }"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td class="text-left">
              <router-link
                :to="{
                  path: '/profiles',
                  query: {
                    location: item.workspaceID,
                    name: item.title,
                    tab: 'all_profiles',
                  },
                }"
                target="_blank"
                rel="noopener noreferrer"
                class="text-primary-600 hover:text-primary-800"
              >
                {{ item.title }}
              </router-link>
              <div class="text-xs text-gray-500 mt-1">
                {{ item.area }}{{ item.city ? `, ${item.city}` : "" }}
              </div>
            </td>
            <td
              class="text-center font-medium"
              :class="getCompletionScoreClass(item.completionScore)"
            >
              {{ item.completionScore }}%
            </td>
            <td
              v-for="header in heatmapHeaders.slice(2)"
              :key="header.value"
              class="text-center"
            >
              <v-icon
                :color="item[header.value] ? 'success' : 'error'"
                :title="getStatusTooltip(header.value, item[header.value])"
              >
                {{
                  item[header.value] ? "mdi-check-circle" : "mdi-close-circle"
                }}
              </v-icon>
            </td>
          </tr>
        </template>
      </v-data-table>
    </div>

    <!-- Missing Field Dialog -->
    <v-dialog v-model="dialogVisible" max-width="800px">
      <v-card>
        <v-card-title class="text-h5 d-flex justify-space-between">
          <span>Missing {{ getFieldDisplayName(selectedField) }}</span>
          <v-btn icon @click="dialogVisible = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            The following
            <span class="text-red-500"
              >{{ missingLocations.length }} locations</span
            >
            are missing this field:
          </p>
          <v-data-table
            :headers="[
              { text: 'Business Name', value: 'title', sortable: true },
              { text: 'Address', value: 'address', sortable: true },
              { text: 'Category', value: 'primaryCategory', sortable: true },
            ]"
            :items="missingLocations"
            :items-per-page="10"
            class="elevation-1"
          >
            <template #item.address="{ item }">
              {{ formatAddress(item) }}
            </template>
            <template #item.primaryCategory="{ item }">
              {{ item.formData?.primaryCategory?.displayName || "None" }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- <v-btn color="primary" text @click="bulkUpdate"> Bulk Update </v-btn> -->
          <v-btn color="gray" text @click="dialogVisible = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/utils/firebase.utils";

export default {
  name: "GMBProfileDashboard",
  props: {
    categories: {
      type: Array,
      required: true,
    },
    locations: {
      type: Array,
      required: true,
    },
    categoriesId: {
      type: Array,
      required: true,
    },
    fetchWorkspaceData: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      showScreenshotHeader: false,
      workspaceData: null,
      filters: {
        location: null,
        category: null,
        missingFields: [],
      },
      dialogVisible: false,
      selectedField: null,
      missingLocations: [],
      healthProfileType: null,
      locationSocialLinks: [
        "attributes/url_instagram",
        "attributes/url_facebook",
        "attributes/url_twitter",
        "attributes/url_linkedin",
        "attributes/url_pinterest",
        "attributes/url_youtube",
      ],
      appliedFilters: {
        location: null,
        category: null,
        missingFields: [],
      },
      heatmapSearch: "",
      sortBy: ["completionScore"],
      sortDesc: [true],
      completionFilter: "all",
      heatmapHeaders: [
        { text: "Business Name", value: "title", sortable: true },
        { text: "Completion %", value: "completionScore", sortable: true },
        { text: "Description", value: "description", sortable: true },
        { text: "Primary Category", value: "primaryCategory", sortable: true },
        {
          text: "Add. Categories",
          value: "additionalCategories",
          sortable: true,
        },
        { text: "Phone Number", value: "phoneNumbers", sortable: true },
        { text: "Website", value: "websiteUri", sortable: true },
        { text: "Business Hours", value: "regularHours", sortable: true },
        // { text: "Opening Date", value: "openingDate", sortable: true },
        // { text: "Social Links", value: "socialLinks", sortable: true },
        { text: "Business Logo", value: "businessLogo", sortable: true },
        { text: "Cover Photo", value: "coverPhoto", sortable: true },
        { text: "Attributes", value: "gmbAttributes", sortable: true },
      ],
      heatmapData: [],
      defaultSort: {
        by: ["completionScore"],
        desc: [true],
      },
    };
  },
  computed: {
    currentWorkspace() {
      return this.$store.getters.currentWorkspace;
    },
    currentWorkspaceName() {
      return (
        this.workspaceData?.name || this.currentWorkspace?.name || "Workspace"
      );
    },
    currentWorkspaceLogo() {
      return (
        this.workspaceData?.logo_original ||
        this.currentWorkspace?.logo_original
      );
    },
    currentDate() {
      const now = new Date();
      return now.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
      });
    },
    filteredLocations() {
      // For dashboard sections, return all locations without any filtering
      if (this.isDashboardSection) {
        return [...this.locations];
      }

      let result = [...this.locations];

      // Apply location ID filter from query params
      if (this.$route.query.location) {
        result = result.filter(
          (location) => location.locationId === this.$route.query.location
        );
      }

      // Apply location filter
      if (this.appliedFilters.location) {
        result = result.filter((location) => {
          if (this.appliedFilters.location === "city") {
            return location.city;
          } else if (this.appliedFilters.location === "state") {
            return location.state;
          }
          return (
            location.city === this.appliedFilters.location ||
            location.state === this.appliedFilters.location
          );
        });
      }

      // Apply category filter
      if (this.appliedFilters.category) {
        result = result.filter((location) => {
          const primaryCategory =
            location.formData?.primaryCategory?.displayName || "";
          return primaryCategory
            .toLowerCase()
            .includes(this.appliedFilters.category.toLowerCase());
        });
      }

      // Apply missing fields filter
      if (
        this.appliedFilters.missingFields &&
        this.appliedFilters.missingFields.length > 0
      ) {
        result = result.filter((location) => {
          return this.appliedFilters.missingFields.some((field) =>
            this.isFieldMissing(location, field)
          );
        });
      }

      return result;
    },

    locationOptions() {
      const cities = [
        ...new Set(
          this.locations.map((location) => location.city).filter(Boolean)
        ),
      ];
      const states = [
        ...new Set(
          this.locations.map((location) => location.state).filter(Boolean)
        ),
      ];

      return [
        { label: "Filter by City", value: "city" },
        { label: "Filter by State", value: "state" },
        ...cities.map((city) => ({ label: city, value: city })),
        ...states.map((state) => ({ label: state, value: state })),
      ];
    },

    categoryOptions() {
      const categories = [
        ...new Set(
          this.locations
            .map((location) => location.formData?.primaryCategory?.displayName)
            .filter(Boolean)
        ),
      ];

      return categories.map((category) => ({
        label: category,
        value: category,
      }));
    },

    fieldOptions() {
      return [
        { label: "Phone Number", value: "phoneNumbers" },
        { label: "Description", value: "description" },
        { label: "Website Link", value: "websiteUri" },
        { label: "Primary Category", value: "primaryCategory" },
        { label: "Additional Categories", value: "additionalCategories" },
        { label: "Opening Hours", value: "regularHours" },
        // { label: "Social Links", value: "socialLinks" },
        { label: "Attributes", value: "gmbAttributes" },
        // { label: "Opening Date", value: "openingDate" },
        { label: "Business Logo", value: "businessLogo" },
        { label: "Cover Photo", value: "coverPhoto" },
      ];
    },

    missingFieldsCount() {
      const counts = {
        phoneNumbers: 0,
        description: 0,
        websiteUri: 0,
        primaryCategory: 0,
        additionalCategories: 0,
        regularHours: 0,
        // socialLinks: 0,
        gmbAttributes: 0,
        // openingDate: 0,
        businessLogo: 0,
        coverPhoto: 0,
      };

      this.filteredLocations.forEach((location) => {
        if (this.isFieldMissing(location, "phoneNumbers"))
          counts.phoneNumbers++;
        if (this.isFieldMissing(location, "description")) counts.description++;
        if (this.isFieldMissing(location, "websiteUri")) counts.websiteUri++;
        if (this.isFieldMissing(location, "primaryCategory"))
          counts.primaryCategory++;
        if (this.isFieldMissing(location, "additionalCategories"))
          counts.additionalCategories++;
        if (this.isFieldMissing(location, "regularHours"))
          counts.regularHours++;
        // if (this.isFieldMissing(location, "socialLinks")) counts.socialLinks++;
        if (this.isFieldMissing(location, "gmbAttributes"))
          counts.gmbAttributes++;
        // if (this.isFieldMissing(location, "openingDate")) counts.openingDate++;
        if (this.isFieldMissing(location, "businessLogo"))
          counts.businessLogo++;
        if (this.isFieldMissing(location, "coverPhoto")) counts.coverPhoto++;
      });

      return counts;
    },

    completeProfilesCount() {
      return this.filteredLocations.filter((location) =>
        this.isProfileComplete(location)
      ).length;
    },

    needsAttentionCount() {
      return this.filteredLocations.filter((location) => {
        const missingFieldsCount =
          (this.isFieldMissing(location, "phoneNumbers") ? 1 : 0) +
          (this.isFieldMissing(location, "description") ? 1 : 0) +
          (this.isFieldMissing(location, "websiteUri") ? 1 : 0) +
          (this.isFieldMissing(location, "primaryCategory") ? 1 : 0) +
          (this.isFieldMissing(location, "additionalCategories") ? 1 : 0) +
          (this.isFieldMissing(location, "regularHours") ? 1 : 0) +
          // (this.isFieldMissing(location, "socialLinks") ? 1 : 0) +
          (this.isFieldMissing(location, "gmbAttributes") ? 1 : 0) +
          // (this.isFieldMissing(location, "openingDate") ? 1 : 0) +
          (this.isFieldMissing(location, "businessLogo") ? 1 : 0) +
          (this.isFieldMissing(location, "coverPhoto") ? 1 : 0);

        return missingFieldsCount > 0 && missingFieldsCount < 3; // 1-2 missing fields
      }).length;
    },

    recentlyUpdatedCount() {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      return this.filteredLocations.filter((location) => {
        if (!location.gmb_last_updated) return false;
        const updateDate = location.gmb_last_updated?.toDate
          ? location.gmb_last_updated.toDate()
          : new Date(location.gmb_last_updated);
        return updateDate >= thirtyDaysAgo;
      }).length;
    },

    needsReauthCount() {
      return this.filteredLocations.filter(
        (location) => location.needs_gmb_reauth
      ).length;
    },

    overallCompleteness() {
      if (!this.filteredLocations.length) return 0;
      return Math.round(
        (this.completeProfilesCount / this.filteredLocations.length) * 100
      );
    },

    computedHeatmapData() {
      // For dashboard sections, use all locations without filtering
      const locationsToUse = this.isDashboardSection
        ? this.locations
        : this.filteredLocations;

      return locationsToUse.map((location) => ({
        id: location.id || "",
        workspaceID: location.wid || location.workspaceId || "",
        locationId: location.locationId || location.id || "",
        title: location.title || location.name || "Unnamed Location",
        area: location.storefrontAddress?.administrativeArea || "",
        city: location.storefrontAddress?.locality || "",
        completionScore: this.calculateCompletionScore(location),
        phoneNumbers: !this.isFieldMissing(location, "phoneNumbers"),
        description: !this.isFieldMissing(location, "description"),
        websiteUri: !this.isFieldMissing(location, "websiteUri"),
        primaryCategory: !this.isFieldMissing(location, "primaryCategory"),
        additionalCategories: !this.isFieldMissing(
          location,
          "additionalCategories"
        ),
        regularHours: !this.isFieldMissing(location, "regularHours"),
        gmbAttributes: !this.isFieldMissing(location, "gmbAttributes"),
        socialLinks: !this.isFieldMissing(location, "socialLinks"),
        openingDate: !this.isFieldMissing(location, "openingDate"),
        businessLogo: !this.isFieldMissing(location, "businessLogo"),
        coverPhoto: !this.isFieldMissing(location, "coverPhoto"),
      }));
    },

    filteredHeatmapData() {
      let data = [...this.computedHeatmapData];

      // Apply search filter if exists
      if (this.heatmapSearch) {
        const searchTerm = this.heatmapSearch.toLowerCase().trim();
        data = data.filter((item) => {
          const title = (item.title || "").toLowerCase();
          const area = (item.area || "").toLowerCase();
          const city = (item.city || "").toLowerCase();
          const fullAddress = `${area}${city ? `, ${city}` : ""}`.toLowerCase();

          return title.includes(searchTerm) || fullAddress.includes(searchTerm);
        });
      }

      // For dashboard sections, only apply completion filter
      if (this.isDashboardSection) {
        switch (this.completionFilter) {
          case "high":
            data = data.filter((item) => item.completionScore >= 80);
            break;
          case "medium":
            data = data.filter(
              (item) => item.completionScore >= 50 && item.completionScore < 80
            );
            break;
          case "low":
            data = data.filter((item) => item.completionScore < 50);
            break;
        }
        return data.sort((a, b) => b.completionScore - a.completionScore);
      }

      // For non-dashboard sections, apply all filters
      switch (this.completionFilter) {
        case "high":
          data = data.filter((item) => item.completionScore >= 80);
          break;
        case "medium":
          data = data.filter(
            (item) => item.completionScore >= 50 && item.completionScore < 80
          );
          break;
        case "low":
          data = data.filter((item) => item.completionScore < 50);
          break;
      }

      return data.sort((a, b) => b.completionScore - a.completionScore);
    },

    isDashboardTab() {
      return (
        this.$route.path === "/profiles/dashboard" ||
        this.$route.query.tab === "dashboard" ||
        !this.$route.query.tab
      );
    },

    hasLocationFilter() {
      return (
        !this.isDashboardSection &&
        this.$route.query.location &&
        this.$route.query.name
      );
    },

    filteredLocationName() {
      return this.$route.query.name || "";
    },

    isDashboardSection() {
      // Check if we're in any of the dashboard sections that should ignore location filter
      return (
        this.$route.path === "/profiles/dashboard" ||
        this.$route.query.tab === "dashboard" ||
        !this.$route.query.tab
      );
    },
  },
  mounted() {
    this.fetchWorkspaceData();
  },
  methods: {
    async fetchWorkspaceData() {
      if (!this.WID || !this.currentUID) {
        console.warn("Cannot fetch workspace data: missing WID or currentUID");
        return;
      }

      try {
        const workspaceRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID
        );
        const workspaceDoc = await getDoc(workspaceRef);

        if (workspaceDoc.exists()) {
          this.workspaceData = workspaceDoc.data();
        } else {
          console.warn("Workspace not found in Firestore for WID:", this.WID);
        }
      } catch (error) {
        console.error("Error fetching workspace data:", error);
      }
    },
    isProfileComplete(location) {
      return (
        !this.isFieldMissing(location, "phoneNumbers") &&
        !this.isFieldMissing(location, "description") &&
        !this.isFieldMissing(location, "websiteUri") &&
        !this.isFieldMissing(location, "primaryCategory") &&
        !this.isFieldMissing(location, "additionalCategories") &&
        !this.isFieldMissing(location, "regularHours") &&
        !this.isFieldMissing(location, "gmbAttributes") &&
        // !this.isFieldMissing(location, "openingDate") &&
        !this.isFieldMissing(location, "businessLogo") &&
        !this.isFieldMissing(location, "coverPhoto") &&
        !this.isFieldMissing(location, "socialLinks")
      );
    },

    isFieldMissing(location, field) {
      switch (field) {
        case "phoneNumbers":
          return !location.phoneNumbers?.primaryPhone;
        case "description":
          return !location.profile?.description;
        case "websiteUri":
          return !location.websiteUri;
        case "primaryCategory":
          return !location.categories?.primaryCategory?.displayName;
        case "additionalCategories":
          return (
            !location.categories?.additionalCategories ||
            location.categories.additionalCategories.length === 0
          );
        case "regularHours":
          return (
            !location.regularHours?.periods ||
            location.regularHours.periods.length === 0
          );
        // case "socialLinks":
        //   return !location?.socialInfo?.found;
        case "gmbAttributes":
          return !this.hasAttributes(location);
        // case "openingDate":
        //   return !location.openInfo?.openingDate;
        //   return false;
        case "businessLogo":
          return !location.mediaItems?.logo?.thumbnailUrl;
        case "coverPhoto":
          return !location.mediaItems?.coverPhoto?.thumbnailUrl;
        default:
          return false;
      }
    },

    applyFilters() {
      this.appliedFilters = { ...this.filters };
    },

    resetFilters() {
      this.filters = {
        location: null,
        category: null,
        missingFields: [],
      };
      this.appliedFilters = { ...this.filters };
    },

    openMissingFieldDialog(field) {
      this.selectedField = field;
      this.missingLocations = this.filteredLocations.filter((location) =>
        this.isFieldMissing(location, field)
      );
      this.dialogVisible = true;
    },

    formatAddress(location) {
      const parts = [];
      if (location.shallowConnect) {
        return location.gmb_shallow_profile_data.address;
      }
      if (location?.storefrontAddress?.addressLines?.[0])
        parts.push(location?.storefrontAddress?.addressLines?.[0]);
      if (location?.storefrontAddress?.locality)
        parts.push(location?.storefrontAddress?.locality);
      if (location?.storefrontAddress?.administrativeArea)
        parts.push(location?.storefrontAddress?.administrativeArea);

      return parts.join(", ") || "Address not available";
    },

    getFieldDisplayName(field) {
      const fieldMap = {
        phoneNumbers: "Phone Number",
        description: "Description",
        websiteUri: "Website Link",
        primaryCategory: "Primary Category",
        additionalCategories: "Additional Categories",
        regularHours: "Opening Hours",
        // socialLinks: "Social Links",
        gmbAttributes: "Attributes",
        // openingDate: "Opening Date",
        businessLogo: "Business Logo",
        coverPhoto: "Cover Photo",
        complete: "Complete Profiles",
        needsAttention: "Profiles Needing Attention",
        recentlyUpdated: "Recently Updated Profiles",
        needsReauth: "Profiles Needing Re-authentication",
      };

      return fieldMap[field] || field;
    },

    goToEditLocation(workspaceId) {
      // Navigate to edit page or open edit modal
      this.$emit("edit-location", workspaceId);
      this.dialogVisible = false;
    },

    bulkUpdate() {
      // Navigate to bulk update tab with pre-selected field
      this.$emit("bulk-update", this.selectedField, this.missingLocations);
      this.dialogVisible = false;
    },

    hasSocialLinks(location) {
      if (
        !location.gmb_attributes ||
        !Array.isArray(location.gmb_attributes) ||
        location.gmb_attributes.length === 0
      ) {
        return false;
      }

      // Check for social media links (url_facebook, url_instagram, url_twitter, etc.)
      return location.gmb_attributes.some((attr) =>
        this.locationSocialLinks.includes(attr.name)
      );
    },

    hasAttributes(location) {
      return (
        location.gmb_attributes &&
        Array.isArray(location.gmb_attributes) &&
        location.gmb_attributes.length > 0
      );
    },

    async captureScreenshot() {
      try {
        // Show a loading indicator
        this.$store.commit("showSnackbar", {
          text: "Preparing screenshot...",
          color: "info",
        });

        // Show header for screenshot
        this.showScreenshotHeader = true;

        // Wait for DOM update
        await this.$nextTick();

        // Get the dashboard element to capture
        const dashboardElement = document.querySelector(".dashboard-container");
        if (!dashboardElement) {
          throw new Error("Dashboard element not found");
        }

        // Use html2canvas to capture the element
        const canvas = await html2canvas(dashboardElement, {
          scale: 2, // Higher quality
          useCORS: true, // Allow cross-origin images
          logging: false,
          backgroundColor: "#ffffff",
        });

        // Hide header after screenshot
        this.showScreenshotHeader = false;

        // Convert to image and download
        const imageData = canvas.toDataURL("image/png");
        const link = document.createElement("a");
        link.href = imageData;

        // Generate filename with current date
        const date = new Date();
        const formattedDate = date.toISOString().split("T")[0];
        link.download = `${this.currentWorkspaceName}-profile-dashboard-${formattedDate}.png`;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Success message
        this.$snackbar.show("Screenshot saved");
      } catch (error) {
        console.error("Error capturing screenshot:", error);
        this.showScreenshotHeader = false; // Ensure header is hidden on error
        this.$snackbar.show("Failed to save screenshot");
      }
    },

    openHealthProfileDialog(type) {
      this.healthProfileType = type;

      switch (type) {
        case "complete":
          this.selectedField = "complete";
          this.missingLocations = this.filteredLocations.filter((location) =>
            this.isProfileComplete(location)
          );
          break;
        case "needsAttention":
          this.selectedField = "needsAttention";
          this.missingLocations = this.filteredLocations.filter((location) => {
            const missingFieldsCount =
              (this.isFieldMissing(location, "phoneNumbers") ? 1 : 0) +
              (this.isFieldMissing(location, "description") ? 1 : 0) +
              (this.isFieldMissing(location, "websiteUri") ? 1 : 0) +
              (this.isFieldMissing(location, "primaryCategory") ? 1 : 0) +
              (this.isFieldMissing(location, "additionalCategories") ? 1 : 0) +
              (this.isFieldMissing(location, "regularHours") ? 1 : 0) +
              (this.isFieldMissing(location, "socialLinks") ? 1 : 0) +
              (this.isFieldMissing(location, "gmbAttributes") ? 1 : 0) +
              // (this.isFieldMissing(location, "openingDate") ? 1 : 0) +
              (this.isFieldMissing(location, "businessLogo") ? 1 : 0) +
              (this.isFieldMissing(location, "coverPhoto") ? 1 : 0);

            return missingFieldsCount > 0 && missingFieldsCount < 3; // 1-2 missing fields
          });
          break;
        case "recentlyUpdated":
          this.selectedField = "recentlyUpdated";
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

          this.missingLocations = this.filteredLocations.filter((location) => {
            if (!location.gmb_last_updated) return false;
            const updateDate = location.gmb_last_updated?.toDate
              ? location.gmb_last_updated.toDate()
              : new Date(location.gmb_last_updated);
            return updateDate >= thirtyDaysAgo;
          });
          break;
        case "needsReauth":
          this.selectedField = "needsReauth";
          this.missingLocations = this.filteredLocations.filter(
            (location) => location.needs_gmb_reauth
          );
          break;
      }

      this.dialogVisible = true;
    },

    getStatusTooltip(field, value) {
      const fieldMap = {
        phoneNumbers: "Phone Number",
        description: "Description",
        websiteUri: "Website Link",
        primaryCategory: "Primary Category",
        additionalCategories: "Additional Categories",
        regularHours: "Opening Hours",
        // socialLinks: "Social Links",
        gmbAttributes: "Attributes",
        openingDate: "Opening Date",
        businessLogo: "Business Logo",
        coverPhoto: "Cover Photo",
        complete: "Complete",
        needsAttention: "Needs Attention",
        recentlyUpdated: "Recently Updated",
        needsReauth: "Needs Re-authentication",
      };

      return `${fieldMap[field]}: ${value ? "Complete" : "Missing"}`;
    },

    calculateCompletionScore(location) {
      const fields = [
        "phoneNumbers",
        "description",
        "websiteUri",
        "primaryCategory",
        "additionalCategories",
        "regularHours",
        // "socialLinks",
        "gmbAttributes",
        // "openingDate",
        "businessLogo",
        "coverPhoto",
      ];

      const completedFields = fields.filter(
        (field) => !this.isFieldMissing(location, field)
      ).length;
      return Math.round((completedFields / fields.length) * 100);
    },

    getCompletionScoreClass(score) {
      if (score >= 80) return "text-green-600";
      if (score >= 50) return "text-amber-600";
      return "text-red-600";
    },

    customSort(items, sortBy, sortDesc) {
      if (!sortBy || !sortBy.length) {
        return items;
      }

      const sortKey = sortBy[0];
      const direction = sortDesc[0] ? -1 : 1;

      return [...items].sort((a, b) => {
        if (sortKey === "completionScore") {
          return direction * (a.completionScore - b.completionScore);
        }

        if (sortKey === "title") {
          return direction * (a.title || "").localeCompare(b.title || "");
        }

        // For boolean values (completion status)
        if (typeof a[sortKey] === "boolean") {
          return (
            direction * (a[sortKey] === b[sortKey] ? 0 : a[sortKey] ? -1 : 1)
          );
        }

        // Default string comparison
        return (
          direction *
          String(a[sortKey] || "").localeCompare(String(b[sortKey] || ""))
        );
      });
    },

    getColumnCompletionPercentage(column) {
      if (!this.computedHeatmapData.length) return 0;
      const completedCount = this.computedHeatmapData.filter(
        (item) => item[column]
      ).length;
      return Math.round(
        (completedCount / this.computedHeatmapData.length) * 100
      );
    },

    setCompletionFilter(filter) {
      this.completionFilter = filter;
    },

    clearLocationFilter() {
      // Remove location and name parameters but keep the tab
      const query = { ...this.$route.query };
      delete query.location;
      delete query.name;

      this.$router.replace({ query });
    },
  },
};
</script>

<style scoped>
/* Screenshot Header Styles */
.screenshot-header {
  margin: -1rem -1rem 1.5rem -1rem; /* Negative margins to extend to container edges */
  position: relative;
  overflow: hidden;
}

.screenshot-header.hidden {
  display: none;
}

.maze-pattern {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
  padding: 1.5rem 2rem;
  overflow: hidden;
}

.maze-pattern::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    /* Horizontal lines */ linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 1px
    ),
    /* Vertical lines */
      linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    /* Diagonal cross pattern */
      linear-gradient(
        45deg,
        transparent 35%,
        rgba(255, 255, 255, 0.05) 35%,
        rgba(255, 255, 255, 0.05) 65%,
        transparent 65%
      ),
    /* Plus symbols */
      radial-gradient(
        circle at 25% 25%,
        rgba(255, 255, 255, 0.1) 2px,
        transparent 3px
      ),
    radial-gradient(
      circle at 75% 75%,
      rgba(255, 255, 255, 0.08) 2px,
      transparent 3px
    );
  background-size: 20px 20px, 20px 20px, 40px 40px, 80px 80px, 120px 120px;
  animation: mazeFloat 20s linear infinite;
}

@keyframes mazeFloat {
  0% {
    transform: translateX(0px);
  }
  100% {
    transform: translateX(40px);
  }
}

.header-content {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.workspace-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.workspace-logo {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.workspace-name {
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  transform: translateY(-10px);
}

.date-text {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  position: relative;
  padding-right: 4rem;
  transform: translateY(-10px);
}

.date-text::after {
  content: "";
  position: absolute;
  right: -3.5rem;
  top: -20px;
  width: 60px;
  height: 80px;
  background-image: 
    /* Row 1 - Top bubbles */ radial-gradient(
      circle at 10px 8px,
      rgba(255, 255, 255, 0.4) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 25px 5px,
      rgba(255, 255, 255, 0.3) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 40px 10px,
      rgba(255, 255, 255, 0.35) 2.5px,
      transparent 3px
    ),
    radial-gradient(
      circle at 55px 7px,
      rgba(255, 255, 255, 0.25) 1px,
      transparent 2px
    ),
    /* Row 2 */
      radial-gradient(
        circle at 8px 20px,
        rgba(255, 255, 255, 0.3) 1.5px,
        transparent 2px
      ),
    radial-gradient(
      circle at 22px 18px,
      rgba(255, 255, 255, 0.4) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 35px 22px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 48px 19px,
      rgba(255, 255, 255, 0.35) 2px,
      transparent 3px
    ),
    /* Row 3 - Middle */
      radial-gradient(
        circle at 12px 32px,
        rgba(255, 255, 255, 0.25) 2px,
        transparent 3px
      ),
    radial-gradient(
      circle at 28px 30px,
      rgba(255, 255, 255, 0.2) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 42px 34px,
      rgba(255, 255, 255, 0.3) 2.5px,
      transparent 3px
    ),
    radial-gradient(
      circle at 52px 31px,
      rgba(255, 255, 255, 0.15) 1px,
      transparent 2px
    ),
    /* Row 4 */
      radial-gradient(
        circle at 6px 44px,
        rgba(255, 255, 255, 0.2) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 20px 42px,
      rgba(255, 255, 255, 0.25) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 36px 46px,
      rgba(255, 255, 255, 0.15) 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 50px 43px,
      rgba(255, 255, 255, 0.2) 1.5px,
      transparent 2px
    ),
    /* Row 5 - Bottom */
      radial-gradient(
        circle at 15px 56px,
        rgba(255, 255, 255, 0.15) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 30px 54px,
      rgba(255, 255, 255, 0.1) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 45px 58px,
      rgba(255, 255, 255, 0.12) 1px,
      transparent 2px
    ),
    /* Additional scattered bubbles */
      radial-gradient(
        circle at 18px 68px,
        rgba(255, 255, 255, 0.08) 1px,
        transparent 2px
      ),
    radial-gradient(
      circle at 38px 70px,
      rgba(255, 255, 255, 0.06) 1px,
      transparent 2px
    );
  background-size: 60px 80px;
}

.date-text::before {
  content: "";
  position: absolute;
  right: -3rem;
  top: -15px;
  width: 50px;
  height: 70px;
  background-image: 
    /* Left side additional bubbles */ radial-gradient(
      circle at 5px 12px,
      rgba(255, 255, 255, 0.2) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 15px 8px,
      rgba(255, 255, 255, 0.15) 1.5px,
      transparent 2px
    ),
    radial-gradient(
      circle at 8px 25px,
      rgba(255, 255, 255, 0.18) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 18px 28px,
      rgba(255, 255, 255, 0.12) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 12px 40px,
      rgba(255, 255, 255, 0.1) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 22px 45px,
      rgba(255, 255, 255, 0.08) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 6px 55px,
      rgba(255, 255, 255, 0.06) 1px,
      transparent 2px
    ),
    radial-gradient(
      circle at 16px 60px,
      rgba(255, 255, 255, 0.05) 1px,
      transparent 2px
    );
  background-size: 50px 70px;
}

/* Existing styles */
.stat-card {
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat {
  padding: 16px;
  border-radius: 8px;
  background-color: #f9f9f9;
  transition: all 0.2s ease;
}

.stat:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.panel {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 12px;
  background-color: #f9f9f9;
}

.profile-heatmap-table {
  border-radius: 8px;
  overflow: hidden;
}

.profile-heatmap-table ::v-deep .v-data-table__wrapper {
  border-radius: 8px;
  overflow: hidden;
}

.profile-heatmap-table ::v-deep tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.profile-heatmap-table ::v-deep .v-data-table-header {
  background-color: #f8fafc;
}

.profile-heatmap-table ::v-deep .v-data-table-header th {
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  font-size: 0.65rem;
  letter-spacing: 0.02em;
  padding: 8px 4px !important;
  height: 60px !important;
  white-space: normal;
  text-align: center;
  line-height: 1.1;
  position: relative;
}

/* Add consistent bottom alignment for header text */
.profile-heatmap-table ::v-deep .v-data-table-header th > div {
  position: absolute;
  bottom: 8px;
  left: 4px;
  right: 4px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  min-height: 32px;
}

.profile-heatmap-table ::v-deep tbody td {
  padding: 8px 4px;
  vertical-align: middle;
  text-align: center;
}

.profile-heatmap-table ::v-deep .v-icon {
  font-size: 18px;
}

.profile-heatmap-table ::v-deep .completion-score {
  font-weight: 600;
}

.profile-heatmap-table ::v-deep .v-data-table__wrapper table {
  table-layout: fixed;
}

/* Adjust specific column widths */
.profile-heatmap-table ::v-deep .v-data-table__wrapper th:first-child {
  width: 220px;
  text-align: left !important;
  padding: 8px 12px !important;
}

.profile-heatmap-table ::v-deep .v-data-table__wrapper th:first-child > div {
  justify-content: flex-start;
  left: 12px;
}

.profile-heatmap-table ::v-deep .v-data-table__wrapper th:nth-child(2) {
  width: 80px;
}

.profile-heatmap-table
  ::v-deep
  .v-data-table__wrapper
  th:not(:first-child):not(:nth-child(2)) {
  width: 70px;
}

/* Ensure first column content is left-aligned */
.profile-heatmap-table ::v-deep tbody td:first-child {
  text-align: left;
  padding-left: 12px;
}

/* Ensure completion score column is properly aligned */
.profile-heatmap-table ::v-deep tbody td:nth-child(2) {
  text-align: center;
  padding: 8px;
}
</style>
